import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import NavigateBeforeRoundedIcon from "@mui/icons-material/NavigateBeforeRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";

import AppBottomSheet from "./AppBottomSheet";
import AppCard from "./AppCard";
import type { MaterialMasterSuggestion } from "../types/materialMasterData";
import { formatStorageAddressDisplay } from "../utils/storageAddress";
import { APP_UI } from "../ui/appUi";

interface Props {
  open: boolean;
  suggestions: MaterialMasterSuggestion[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onClose: () => void;
  onSelect: (suggestion: MaterialMasterSuggestion) => void;
}

export default function MaterialMasterSuggestionsDialog({
  open,
  suggestions,
  total,
  page,
  pageSize,
  onPageChange,
  onClose,
  onSelect,
}: Props) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : page * pageSize + 1;
  const end = Math.min(total, (page + 1) * pageSize);

  return (
    <AppBottomSheet
      open={open}
      onClose={onClose}
      title="Materialvorschläge"
      subtitle={total === 0 ? "Keine Treffer" : `${total.toLocaleString("de-DE")} Treffer insgesamt`}
      leading={<SearchRoundedIcon sx={{ color: APP_UI.accent }} />}
      maxHeight="92dvh"
    >
      <Stack direction="row" spacing={0.4} sx={{ alignItems: "center" }}>
        <Typography variant="caption" sx={{ color: APP_UI.textMuted, flex: 1 }}>
          {total === 0 ? "Keine Treffer" : `${start}–${end} Treffer`}
        </Typography>
        <IconButton aria-label="Erste Trefferseite" title="Erste Seite" size="small" disabled={page <= 0} onClick={() => onPageChange(0)} sx={{ color: APP_UI.accent }}><FirstPageRoundedIcon /></IconButton>
        <IconButton aria-label="Vorherige Trefferseite" title="Vorherige Seite" size="small" disabled={page <= 0} onClick={() => onPageChange(page - 1)} sx={{ color: APP_UI.accent }}><NavigateBeforeRoundedIcon /></IconButton>
        <Typography variant="caption" sx={{ color: APP_UI.text, minWidth: 42, textAlign: "center", fontWeight: 850 }}>{page + 1}/{pageCount}</Typography>
        <IconButton aria-label="Nächste Trefferseite" title="Nächste Seite" size="small" disabled={page + 1 >= pageCount} onClick={() => onPageChange(page + 1)} sx={{ color: APP_UI.accent }}><NavigateNextRoundedIcon /></IconButton>
        <IconButton aria-label="Letzte Trefferseite" title="Letzte Seite" size="small" disabled={page + 1 >= pageCount} onClick={() => onPageChange(pageCount - 1)} sx={{ color: APP_UI.accent }}><LastPageRoundedIcon /></IconButton>
      </Stack>

      <Stack spacing={0.75}>
        {suggestions.map((suggestion, index) => (
          <AppCard key={suggestion.record.materialNumber} sx={{ bgcolor: index === 0 ? APP_UI.accentSoft : APP_UI.card }}>
            <Typography variant="subtitle2" sx={{ color: APP_UI.accent, fontWeight: 900 }}>{suggestion.record.materialNumber}</Typography>
            <Typography variant="body2" sx={{ color: APP_UI.text, fontWeight: 750 }}>{suggestion.record.description}</Typography>
            <Typography variant="caption" sx={{ color: APP_UI.textMuted, display: "block" }}>
              {suggestion.record.type}{suggestion.record.manufacturer ? ` · ${suggestion.record.manufacturer}` : ""}
            </Typography>
            {suggestion.record.storageLocations.length > 0 && (
              <Typography variant="caption" sx={{ color: APP_UI.textMuted, display: "block", mt: 0.25 }}>
                Lagerort: {suggestion.record.storageLocations.map(formatStorageAddressDisplay).join(" · ")}
              </Typography>
            )}
            <Button variant="outlined" onClick={() => onSelect(suggestion)} sx={{ mt: 0.7, borderRadius: 999, color: APP_UI.accent, borderColor: APP_UI.accentBorder, textTransform: "none", fontWeight: 800 }}>
              Übernehmen
            </Button>
          </AppCard>
        ))}
        {suggestions.length === 0 && (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: APP_UI.textMuted }}>Keine Treffer vorhanden.</Typography>
          </Box>
        )}
      </Stack>
    </AppBottomSheet>
  );
}
